"use client";

import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { OUTLINE_NUMBERER_STRINGS } from "@/components/tools/outline-numberer";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import type { OutlineResult } from "@/lib/tool-kit/qualitative-text/outline-number";

type Strings = typeof OUTLINE_NUMBERER_STRINGS;

export function OutlineNumbererResults({
  result,
  strings,
  definedLevels,
}: {
  result: OutlineResult;
  strings: Strings;
  /** Levels the chosen style defines before it starts again. */
  definedLevels: number;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      {result.items.length === 0 ? <Notice>{strings.noItems}</Notice> : null}
      {result.items.length > 0 ? (
        <>
          <StatGrid>
            <StatCard
              emphasis
              label={strings.statItems}
              value={fmt(result.items.length)}
            />
            <StatCard label={strings.statDepth} value={fmt(result.depth)} />
            <StatCard
              label={strings.statTop}
              value={fmt(result.levelCounts[0] ?? 0)}
            />
          </StatGrid>
          {result.wrapped ? (
            <Notice>
              {tPlural(strings.wrappedNotice, locale, definedLevels, {
                levels: fmt(definedLevels),
              })}
            </Notice>
          ) : null}
          {result.snapped > 0 ? (
            <Notice>
              {tPlural(strings.snappedNotice, locale, result.snapped, {
                count: fmt(result.snapped),
              })}
            </Notice>
          ) : null}
          <div className="space-y-2">
            <label className={LABEL} htmlFor="on-output">
              {strings.outputLabel}
            </label>
            <textarea
              className={TEXTAREA}
              id="on-output"
              readOnly
              rows={10}
              value={result.output}
            />
            <div className="flex flex-wrap gap-3">
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy}
                text={result.output}
              />
              <button
                className={BUTTON_SECONDARY}
                onClick={() => downloadText(strings.filename, result.output)}
                type="button"
              >
                {strings.download}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.levelsHeading}</h3>
            <DataTable
              head={[strings.colLevel, strings.colFirst, strings.colItems]}
              rows={result.firstLabels.map((label, index) => [
                fmt(index + 1),
                label,
                fmt(result.levelCounts[index] ?? 0),
              ])}
            />
          </div>
        </>
      ) : null}
    </section>
  );
}
